import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCorners,
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { WorkspaceProjectColumn } from "@/hooks/useWorkspaceProjects";
import type { WorkspaceTask } from "@/hooks/useWorkspaceTasks";
import { UNCATEGORIZED_COLUMN_ID } from "@/hooks/useProjectKanbanTasks";
import type { KanbanReorderUpdate } from "@/hooks/useProjectKanbanTasks";
import { TasksKanbanCard } from "./TasksKanbanCard";

const ORDER_GAP = 1000;

type KanbanColumn = WorkspaceProjectColumn | { id: typeof UNCATEGORIZED_COLUMN_ID; name: string; color: string };

interface TasksKanbanProps {
  columns: WorkspaceProjectColumn[];
  columnsLoading?: boolean;
  tasksByColumn: Map<string, WorkspaceTask[]>;
  loading: boolean;
  isAdmin: boolean;
  currentMemberId: string | null;
  searchQuery?: string;
  onOptimisticReorder: (updates: KanbanReorderUpdate[]) => void;
  onPersistReorder: (updates: KanbanReorderUpdate[]) => Promise<void>;
  onComplete: (task: WorkspaceTask) => void;
  onReopen: (taskId: string) => void;
  onView: (task: WorkspaceTask) => void;
  onCancelRequest?: (task: WorkspaceTask) => void;
  onOpenColumnManager?: () => void;
  isAdminCanManageColumns?: boolean;
  onAddTask?: (columnId: string) => void;
  onCreateColumn?: (input: { name: string; color: string }) => Promise<unknown>;
}

function KanbanColumnView({
  column,
  tasks,
  isAdmin,
  currentMemberId,
  onComplete,
  onReopen,
  onView,
  onCancelRequest,
  dragDisabled,
  onAddTask,
  className,
  hideHeader,
  moveColumns,
  onMoveToColumn,
}: {
  column: KanbanColumn;
  tasks: WorkspaceTask[];
  isAdmin: boolean;
  currentMemberId: string | null;
  onComplete: (task: WorkspaceTask) => void;
  onReopen: (taskId: string) => void;
  onView: (task: WorkspaceTask) => void;
  onCancelRequest?: (task: WorkspaceTask) => void;
  dragDisabled: boolean;
  onAddTask?: (columnId: string) => void;
  className?: string;
  hideHeader?: boolean;
  moveColumns?: { id: string; name: string; color: string }[];
  onMoveToColumn?: (task: WorkspaceTask, columnId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const columnIds = tasks.map((t) => t.id);

  return (
    <Card
      ref={setNodeRef}
      className={cn(
        "flex flex-col h-full min-h-0 self-stretch w-[280px] flex-shrink-0 transition-colors",
        className,
        isOver && "ring-2 ring-primary",
      )}
    >
      {!hideHeader && (
      <CardHeader className="pb-2 flex-shrink-0 px-3 pt-3 border-b">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <div className={cn("w-2 h-2 rounded-full shrink-0", column.color)} />
          <span className="truncate flex-1">{column.name}</span>
          <span className="text-xs text-muted-foreground tabular-nums">{tasks.length}</span>
        </CardTitle>
      </CardHeader>
      )}
      {onAddTask && column.id !== UNCATEGORIZED_COLUMN_ID && (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-start text-muted-foreground hover:text-foreground shrink-0 px-3 mx-0",
            hideHeader ? "h-11 rounded-xl border mb-2" : "h-8 rounded-none border-b",
          )}
          onClick={() => onAddTask(column.id)}
        >
          <Plus className="h-4 w-4 mr-1.5 shrink-0" />
          Adicionar tarefa
        </Button>
      )}
      <CardContent className="flex-1 overflow-y-auto min-h-0 p-2 space-y-2">
        <SortableContext items={columnIds} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TasksKanbanCard
              key={task.id}
              task={task}
              isAdmin={isAdmin}
              currentMemberId={currentMemberId}
              onComplete={onComplete}
              onReopen={onReopen}
              onView={onView}
              onCancelRequest={onCancelRequest}
              dragDisabled={dragDisabled}
              moveColumns={moveColumns}
              onMoveToColumn={onMoveToColumn}
            />
          ))}
        </SortableContext>
        {tasks.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">Nenhuma tarefa</p>
        )}
      </CardContent>
    </Card>
  );
}

function AddColumnGhost({
  onCreateColumn,
  onOpenColumnManager,
}: {
  onCreateColumn?: (input: { name: string; color: string }) => Promise<unknown>;
  onOpenColumnManager?: () => void;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !onCreateColumn) return;
    setSubmitting(true);
    try {
      const result = await onCreateColumn({ name: name.trim(), color: "bg-blue-500" });
      if (result) {
        setName("");
        setIsAdding(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setIsAdding(false);
  };

  if (isAdding && onCreateColumn) {
    return (
      <Card className="flex flex-col h-full min-h-0 self-stretch w-[280px] flex-shrink-0">
        <CardContent className="flex flex-col gap-2 p-3">
          <Input
            placeholder="Nome da coluna"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSubmit();
              if (e.key === "Escape") handleCancel();
            }}
            autoFocus
            disabled={submitting}
            className="h-8"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 h-8"
              disabled={!name.trim() || submitting}
              onClick={() => void handleSubmit()}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar"}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={handleCancel} disabled={submitting}>
              Cancelar
            </Button>
          </div>
          {onOpenColumnManager && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground"
              onClick={() => {
                handleCancel();
                onOpenColumnManager();
              }}
            >
              Gerenciar colunas...
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (onCreateColumn) setIsAdding(true);
        else onOpenColumnManager?.();
      }}
      className="flex flex-col items-center justify-center h-full min-h-0 self-stretch w-[48px] flex-shrink-0 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30 transition-colors text-muted-foreground hover:text-foreground"
      title="Adicionar coluna"
    >
      <Plus className="h-5 w-5" />
    </button>
  );
}

function countKanbanTasks(tasksByColumn: Map<string, WorkspaceTask[]>): number {
  let total = 0;
  for (const [, list] of tasksByColumn) total += list.length;
  return total;
}

function buildColumnOrderUpdates(
  columnId: string,
  orderedTasks: WorkspaceTask[],
): KanbanReorderUpdate[] {
  return orderedTasks.map((task, idx) => ({
    taskId: task.id,
    columnId,
    orderIndex: (idx + 1) * ORDER_GAP,
  }));
}

export function TasksKanban({
  columns,
  columnsLoading = false,
  tasksByColumn,
  loading,
  isAdmin,
  currentMemberId,
  searchQuery = "",
  onOptimisticReorder,
  onPersistReorder,
  onComplete,
  onReopen,
  onView,
  onCancelRequest,
  onOpenColumnManager,
  isAdminCanManageColumns = false,
  onAddTask,
  onCreateColumn,
}: TasksKanbanProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragTasks, setDragTasks] = useState<Map<string, WorkspaceTask[]> | null>(null);
  const [narrow, setNarrow] = useState(false);
  const [mobileColumnId, setMobileColumnId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const displayColumns: KanbanColumn[] = useMemo(() => {
    const cols: KanbanColumn[] = [...columns];
    const uncategorized = tasksByColumn.get(UNCATEGORIZED_COLUMN_ID) ?? [];
    if (uncategorized.length > 0 || columns.length === 0) {
      cols.push({
        id: UNCATEGORIZED_COLUMN_ID,
        name: "Sem coluna",
        color: "bg-gray-400",
      });
    }
    return cols;
  }, [columns, tasksByColumn]);

  const visibleColumns = useMemo(
    () =>
      displayColumns.filter(
        (c) => c.id !== UNCATEGORIZED_COLUMN_ID || (tasksByColumn.get(UNCATEGORIZED_COLUMN_ID)?.length ?? 0) > 0,
      ),
    [displayColumns, tasksByColumn],
  );

  useEffect(() => {
    if (visibleColumns.length === 0) {
      setMobileColumnId(null);
      return;
    }
    if (!mobileColumnId || !visibleColumns.some((c) => c.id === mobileColumnId)) {
      setMobileColumnId(visibleColumns[0].id);
    }
  }, [visibleColumns, mobileColumnId]);

  const effectiveMap = dragTasks ?? tasksByColumn;

  const allTasks = useMemo(() => {
    const list: WorkspaceTask[] = [];
    for (const [, tasks] of effectiveMap) list.push(...tasks);
    return list;
  }, [effectiveMap]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const resolveColumnId = useCallback(
    (id: string): string | null => {
      if (displayColumns.some((c) => c.id === id)) return id;
      for (const [colId, tasks] of effectiveMap) {
        if (tasks.some((t) => t.id === id)) return colId;
      }
      return null;
    },
    [displayColumns, effectiveMap],
  );

  const getColumnTasks = useCallback(
    (columnId: string) => effectiveMap.get(columnId) ?? [],
    [effectiveMap],
  );

  const moveableColumns = useMemo(
    () => columns.map((c) => ({ id: c.id, name: c.name, color: c.color })),
    [columns],
  );

  const handleMoveToColumn = useCallback(
    (task: WorkspaceTask, columnId: string) => {
      if (columnId === UNCATEGORIZED_COLUMN_ID) return;
      const currentCol = task.project_column_id ?? UNCATEGORIZED_COLUMN_ID;
      if (currentCol === columnId) return;
      const targetList = getColumnTasks(columnId);
      const lastOrder = targetList[targetList.length - 1]?.board_order_index ?? 0;
      const update = {
        taskId: task.id,
        columnId,
        orderIndex: lastOrder + ORDER_GAP,
      };
      onOptimisticReorder([update]);
      setMobileColumnId(columnId);
      void onPersistReorder([update]).catch((err: unknown) => {
        onOptimisticReorder([
          {
            taskId: task.id,
            columnId: currentCol,
            orderIndex: task.board_order_index ?? 0,
          },
        ]);
        toast({
          title: "Erro ao mover tarefa",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      });
    },
    [getColumnTasks, onOptimisticReorder, onPersistReorder, toast],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setDragTasks(new Map(tasksByColumn));
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !dragTasks) return;

    const activeTaskId = String(active.id);
    const overId = String(over.id);
    const targetColumnId = resolveColumnId(overId);
    if (!targetColumnId || targetColumnId === UNCATEGORIZED_COLUMN_ID) return;

    setDragTasks((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);

      let activeTask: WorkspaceTask | undefined;
      let sourceColId: string | null = null;

      for (const [colId, tasks] of next) {
        const found = tasks.find((t) => t.id === activeTaskId);
        if (found) {
          activeTask = found;
          sourceColId = colId;
          break;
        }
      }
      if (!activeTask || !sourceColId) return prev;

      const isOverColumn = displayColumns.some((c) => c.id === overId);

      if (sourceColId !== targetColumnId) {
        next.set(
          sourceColId,
          (next.get(sourceColId) ?? []).filter((t) => t.id !== activeTaskId),
        );
      }

      let targetList =
        sourceColId === targetColumnId
          ? [...(next.get(targetColumnId) ?? [])]
          : [...(next.get(targetColumnId) ?? [])].filter((t) => t.id !== activeTaskId);

      const taskWithColumn = { ...activeTask, project_column_id: targetColumnId };
      if (!targetList.some((t) => t.id === activeTaskId)) {
        targetList.push(taskWithColumn);
      }

      const oldIndex = targetList.findIndex((t) => t.id === activeTaskId);
      if (oldIndex < 0) return prev;

      let newIndex: number;
      if (isOverColumn) {
        newIndex = targetList.length - 1;
      } else {
        const overIndex = targetList.findIndex((t) => t.id === overId);
        if (overIndex < 0) return prev;
        newIndex = overIndex;
      }

      if (oldIndex === newIndex && sourceColId === targetColumnId) return prev;

      const reordered = arrayMove(targetList, oldIndex, newIndex).map((t) =>
        t.id === activeTaskId ? taskWithColumn : t,
      );
      next.set(targetColumnId, reordered);

      return next;
    });
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveId(null);
    setDragTasks(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const workingMap = dragTasks ?? tasksByColumn;

    if (!over) {
      setActiveId(null);
      setDragTasks(null);
      return;
    }

    const activeTaskId = String(active.id);
    const overId = String(over.id);
    const targetColumnId = resolveColumnId(overId);

    if (!targetColumnId || targetColumnId === UNCATEGORIZED_COLUMN_ID) {
      setActiveId(null);
      setDragTasks(null);
      return;
    }

    const originalTask = [...tasksByColumn.values()].flat().find((t) => t.id === activeTaskId);

    if (!originalTask) {
      setActiveId(null);
      setDragTasks(null);
      return;
    }

    const previousColumnId = originalTask.project_column_id ?? UNCATEGORIZED_COLUMN_ID;
    const previousOrderIndex = originalTask.board_order_index ?? 0;

    const isOverColumn = displayColumns.some((c) => c.id === overId);
    let columnTasks = [...(workingMap.get(targetColumnId) ?? [])];

    if (!columnTasks.some((t) => t.id === activeTaskId)) {
      columnTasks.push({
        ...originalTask,
        project_column_id: targetColumnId,
      });
    }

    const activeIndex = columnTasks.findIndex((t) => t.id === activeTaskId);
    if (activeIndex >= 0 && !isOverColumn) {
      const overIndex = columnTasks.findIndex((t) => t.id === overId);
      if (overIndex >= 0 && activeIndex !== overIndex) {
        columnTasks = arrayMove(columnTasks, activeIndex, overIndex);
      }
    } else if (isOverColumn && activeIndex >= 0 && activeIndex !== columnTasks.length - 1) {
      columnTasks = arrayMove(columnTasks, activeIndex, columnTasks.length - 1);
    }

    const orderUpdates = buildColumnOrderUpdates(targetColumnId, columnTasks);
    const activeUpdate = orderUpdates.find((u) => u.taskId === activeTaskId);
    const columnChanged = previousColumnId !== targetColumnId;
    const orderChanged = activeUpdate ? activeUpdate.orderIndex !== previousOrderIndex : false;

    if (!columnChanged && !orderChanged) {
      setActiveId(null);
      setDragTasks(null);
      return;
    }

    const persistedUpdates: KanbanReorderUpdate[] = [];
    for (const update of orderUpdates) {
      const orig = [...tasksByColumn.values()].flat().find((t) => t.id === update.taskId);
      if (!orig) continue;
      const origColumnId = orig.project_column_id ?? UNCATEGORIZED_COLUMN_ID;
      const origOrder = orig.board_order_index ?? 0;
      const columnPatch = update.taskId === activeTaskId && columnChanged;
      const orderPatch = origOrder !== update.orderIndex || origColumnId !== update.columnId;
      if (!columnPatch && !orderPatch) continue;
      persistedUpdates.push(update);
    }

    const snapshot = [...tasksByColumn.values()].flat().map((t) => ({
      id: t.id,
      project_column_id: t.project_column_id,
      board_order_index: t.board_order_index,
    }));

    onOptimisticReorder(persistedUpdates.length > 0 ? persistedUpdates : orderUpdates);
    setActiveId(null);
    setDragTasks(null);

    try {
      await onPersistReorder(persistedUpdates.length > 0 ? persistedUpdates : orderUpdates);
    } catch (err: unknown) {
      onOptimisticReorder(
        snapshot.map((t) => ({
          taskId: t.id,
          columnId: t.project_column_id ?? UNCATEGORIZED_COLUMN_ID,
          orderIndex: t.board_order_index ?? 0,
        })),
      );
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao mover tarefa",
        description: message,
        variant: "destructive",
      });
    }
  };

  const activeTask = activeId ? allTasks.find((t) => t.id === activeId) : null;
  const totalTaskCount = countKanbanTasks(tasksByColumn);

  if ((loading || columnsLoading) && totalTaskCount === 0 && columns.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (columns.length === 0 && totalTaskCount === 0) {
    return (
      <Card className="p-10 text-center space-y-3">
        <p className="text-muted-foreground">
          Este projeto ainda não tem colunas Kanban.
        </p>
        {isAdminCanManageColumns && onOpenColumnManager && (
          <Button onClick={onOpenColumnManager}>Adicionar colunas</Button>
        )}
      </Card>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden">
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        {narrow ? (
          <div className="flex flex-col flex-1 min-h-0">
            <div
              className="flex gap-1.5 overflow-x-auto py-1 shrink-0 snap-x snap-mandatory"
              role="tablist"
              aria-label="Colunas do quadro"
            >
              {visibleColumns.map((column) => {
                const count = getColumnTasks(column.id).length;
                const active = column.id === mobileColumnId;
                return (
                  <button
                    key={column.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setMobileColumnId(column.id)}
                    className={cn(
                      "snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 h-8 text-xs font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "bg-primary/10 text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    <span className={cn("h-1.5 w-1.5 rounded-full", column.color)} />
                    <span className="max-w-[9rem] truncate">{column.name}</span>
                    <span className="tabular-nums text-[10px] opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            {visibleColumns
              .filter((column) => column.id === mobileColumnId)
              .map((column) => (
                <KanbanColumnView
                  key={column.id}
                  column={column}
                  tasks={getColumnTasks(column.id)}
                  isAdmin={isAdmin}
                  currentMemberId={currentMemberId}
                  onComplete={onComplete}
                  onReopen={onReopen}
                  onView={onView}
                  onCancelRequest={onCancelRequest}
                  dragDisabled
                  onAddTask={onAddTask}
                  hideHeader
                  className="w-full min-w-0 border-0 shadow-none bg-transparent"
                  moveColumns={moveableColumns.filter((c) => c.id !== column.id)}
                  onMoveToColumn={handleMoveToColumn}
                />
              ))}
          </div>
        ) : (
          <div className="flex flex-1 min-h-0 h-full items-stretch gap-3 overflow-x-auto overflow-y-hidden">
            {visibleColumns.map((column) => (
              <KanbanColumnView
                key={column.id}
                column={column}
                tasks={getColumnTasks(column.id)}
                isAdmin={isAdmin}
                currentMemberId={currentMemberId}
                onComplete={onComplete}
                onReopen={onReopen}
                onView={onView}
                onCancelRequest={onCancelRequest}
                dragDisabled={Boolean(searchQuery.trim())}
                onAddTask={onAddTask}
                moveColumns={moveableColumns.filter((c) => c.id !== column.id)}
                onMoveToColumn={handleMoveToColumn}
              />
            ))}
            {isAdminCanManageColumns && (onCreateColumn || onOpenColumnManager) && (
              <AddColumnGhost
                onCreateColumn={onCreateColumn}
                onOpenColumnManager={onOpenColumnManager}
              />
            )}
          </div>
        )}

        <DragOverlay>
          {activeTask ? (
            <div className="w-[260px] opacity-90">
              <TasksKanbanCard
                task={activeTask}
                isAdmin={isAdmin}
                currentMemberId={currentMemberId}
                onComplete={onComplete}
                onReopen={onReopen}
                onView={onView}
                onCancelRequest={onCancelRequest}
                dragDisabled
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      </div>
    </div>
  );
}
